import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { formatEUR } from "@/lib/api";
import { getSheetInfo } from "@/lib/materials";

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const sheet = getSheetInfo(product);
  const discount = product.old_price
    ? Math.round((1 - product.price / product.old_price) * 100)
    : null;

  const handleAdd = (e) => {
    e.preventDefault();
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      unit: product.unit,
    });
    toast.success("Dodano v košarico", { description: product.name });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4 }}
      data-testid={`product-card-${product.id}`}
      className="group bg-white rounded-2xl border border-[#E4E0D7] overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
    >
      <Link to={`/izdelek/${product.id}`} data-testid={`product-link-${product.id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden bg-[#F3EFE6]">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {discount && (
            <span data-testid={`product-discount-${product.id}`} className="absolute top-3 left-3 bg-[#D97706] text-white text-xs font-bold px-2.5 py-1 rounded-full font-tech">
              −{discount}%
            </span>
          )}
          {product.badge && !discount && (
            <span className="absolute top-3 left-3 bg-[#2D4030] text-[#FBF9F5] text-xs font-semibold px-2.5 py-1 rounded-full">
              {product.badge}
            </span>
          )}
        </div>
        <div className="p-4">
          <h3 className="text-sm font-semibold text-[#1A1D1A] leading-snug line-clamp-2 min-h-[2.5rem]">
            {product.name}
          </h3>
          {product.dimensions && (
            <p className="mt-1 text-xs text-stone-500 font-tech">{product.dimensions}</p>
          )}
          <div className="mt-3 flex items-end justify-between gap-2">
            <div>
              <div className="flex items-baseline gap-2">
                <span data-testid={`product-price-${product.id}`} className="font-tech font-bold text-lg text-[#1A1D1A]">
                  {formatEUR(sheet ? sheet.unitPrice : product.price)}
                </span>
                {(sheet ? sheet.oldUnitPrice : product.old_price) && (
                  <span className="font-tech text-sm text-stone-400 line-through">
                    {formatEUR(sheet ? sheet.oldUnitPrice : product.old_price)}
                  </span>
                )}
              </div>
              {sheet ? (
                <span className="text-[11px] text-stone-400">na {sheet.mode === "tm" ? "tekoči meter" : "m²"}, z DDV · cela plošča {formatEUR(product.price)}</span>
              ) : (
                <span className="text-[11px] text-stone-400">na {product.unit}, z DDV</span>
              )}
            </div>
            <button
              data-testid={`add-to-cart-${product.id}`}
              onClick={handleAdd}
              className="shrink-0 w-10 h-10 rounded-full bg-[#2D4030] text-[#FBF9F5] flex items-center justify-center hover:bg-[#B45309] transition-colors"
              aria-label="Dodaj v košarico"
            >
              <ShoppingCart className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
